/**
 * Formik-bound QueryBuilder wrapper for exposure rules.
 * Sibling of `CommunicationConditionsQueryBuilderFormik` — same skeleton (read
 * `values[fieldId]`, gate on `submitCount`, compute prereqs, call the shared
 * registry hook, fire `onFieldsLoaded`, render the pure QB) but parameterised
 * over the exposure_rule rule_set_type.
 *
 * Differences from the comms sibling:
 *  - No `event_key`. Exposure rules don't dispatch via an event — they're
 *    iterated over the claim's IPs at FNOL by a dedicated evaluator. The
 *    catalog endpoint returns the unfiltered shape when no event_key is
 *    supplied.
 *  - Sub-org model: exposures use a single `sub_organization_id` field, not a
 *    multi-select with "all". The wrapper takes a nullable id and wraps it in
 *    an array for the hook; `isAllSubOrgs` is always false.
 *  - Prereq predicate: LOB + sub-org. Coverage is a rule attribute, not a
 *    catalog filter — left out of the gate intentionally.
 *
 * The composed pure `RuleConditionsQueryBuilder` writes its tree into Formik
 * under `fieldId` (default `'conditions_json'`).
 */

import React, { useEffect } from 'react';
import type { RuleGroupType } from 'react-querybuilder';
import { getIn, useFormikContext } from 'formik';

import useOrganization from '../../../../OrganizationContext';
import RuleConditionsQueryBuilder from '../../Conditions/QueryBuilder/RuleConditionsQueryBuilder';
import type { FieldDefinitionDto } from '../../Conditions/QueryBuilder/types';
import { useRuleSetConditionFieldRegistry } from '../../Conditions/QueryBuilder/useRuleSetConditionFieldRegistry';

const EXPOSURE_RULE_TYPE = 'exposure_rule';

interface ExposureConditionsQueryBuilderFormikProps {
  /** Formik path holding the `conditions_json` tree (default: `'conditions_json'`). */
  fieldId?: string;
  lob: string;
  /** Single sub-org id from the exposures form, or null/empty when not yet
   *  picked / when sub-orgs are disabled at the org level. */
  subOrganizationId: number | null;
  /** Lets the parent form mirror the registry into its own state for use in
   *  Formik's form-level `validate` prop. */
  onFieldsLoaded?: (fields: FieldDefinitionDto[]) => void;
  /** Surfaces the catalog-fetch loading state so the parent dialog can disable
   *  Save while fields are still in flight — mirrors the legacy `Conditions`
   *  control's `setIsLoading` prop. When prereqs aren't met the fetcher is
   *  idle (isLoading=false), so Save behaves normally outside of fetches. */
  onLoadingChange?: (isLoading: boolean) => void;
}

interface PrerequisiteCheck {
  /** True when LOB + (sub-org-set OR sub-orgs-disabled) both hold. */
  prerequisitesMet: boolean;
  /** Copy shown in place of the QB while prerequisites aren't met; `null` when met. */
  prerequisiteMessage: string | null;
}

/** Compute the exposures-specific prerequisite predicate + placeholder copy.
 *  Event is fixed, so only LOB + sub-org need checking. Sub-org-enabled orgs
 *  need an explicit sub-org pick (exposures has no "all sub-orgs" option). */
const checkPrerequisites = (
  lob: string,
  subOrganizationId: number | null,
  subOrgsEnabled: boolean
): PrerequisiteCheck => {
  const hasOrgScope = !subOrgsEnabled || (subOrganizationId !== null && subOrganizationId !== undefined);
  if (lob && hasOrgScope) {
    return { prerequisitesMet: true, prerequisiteMessage: null };
  }
  const subOrgPart = subOrgsEnabled ? ' and Sub-Organization' : '';
  return {
    prerequisitesMet: false,
    prerequisiteMessage: `Select a Line of Business${subOrgPart} to configure conditions.`,
  };
};

const ExposureConditionsQueryBuilderFormik: React.FC<ExposureConditionsQueryBuilderFormikProps> = ({
  fieldId = 'conditions_json',
  lob,
  subOrganizationId,
  onFieldsLoaded,
  onLoadingChange,
}) => {
  const { values, setFieldValue, submitCount } = useFormikContext();
  const value = (getIn(values, fieldId) as RuleGroupType | null | undefined) ?? null;

  // `useOrganization` is exported from a JSX file without types; cast at the boundary.
  const { organizationId, subOrganizationEnabled } = useOrganization() as {
    organizationId: number;
    subOrganizationEnabled?: boolean;
  };

  const { prerequisitesMet, prerequisiteMessage } = checkPrerequisites(
    lob,
    subOrganizationId,
    !!subOrganizationEnabled
  );

  const subOrgIds = subOrganizationId !== null && subOrganizationId !== undefined ? [subOrganizationId] : [];

  const { fields, isLoading } = useRuleSetConditionFieldRegistry({
    organizationId,
    enabled: prerequisitesMet,
    ruleSetType: EXPOSURE_RULE_TYPE,
    // Single-LOB consumer: the rule carries one LOB, so the list-shaped catalog
    // scope is that one LOB. `enabled` already gates on it being picked.
    lobs: [lob],
    isAllSubOrgs: false,
    subOrgIds,
  });

  // Intentionally only depends on `fields` — `onFieldsLoaded` identity is
  // irrelevant. Including it would silently infinite-loop if a caller passes
  // a non-memoized inline arrow.
  useEffect(() => {
    onFieldsLoaded?.(fields);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fields]);

  // Same callback-identity caveat as onFieldsLoaded: depend on the boolean
  // value only, not the callback. Parent dialogs typically wire this to a
  // `setIsLoading` setState from useState, whose identity is stable anyway.
  useEffect(() => {
    onLoadingChange?.(isLoading);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isLoading]);

  return (
    <RuleConditionsQueryBuilder
      value={value}
      onChange={(q) => setFieldValue(fieldId, q)}
      shouldShowErrors={submitCount > 0}
      fields={fields}
      isLoading={isLoading}
      prerequisiteMessage={prerequisiteMessage}
      emptyMessage="No condition fields available for this LOB."
    />
  );
};

export default ExposureConditionsQueryBuilderFormik;
